import * as yup from 'yup';

import { AuspiceDto, Institution, InstitutionTypeDto } from '../common/Types';
import { Card, CardBody, CardFooter, Form, FormGroup, Input, Label } from 'reactstrap';
import React, { useEffect, useState } from 'react';

import Button from './Button';
import { Redirect } from 'react-router-dom';
import { institutionService } from '../services/InstitutionService';
import { toast } from 'react-toastify';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Countries, States } from '../common/Data';

interface NewInstitutionProps {
    onSaved: (newInstitution: Institution) => void;
    onCanceled: () => void;
}

const NewInstitution = (props: NewInstitutionProps) => {
    const { onCanceled } = props;

    const [navigateToId, setNavigateToId] = useState<number>();
    const [institutionTypes, setInstitutionTypes] = useState<InstitutionTypeDto[]>();
    const [auspices, setAuspices] = useState<AuspiceDto[]>();
    const [isInternational, setIsInternational] = useState<boolean>(false);

    useEffect(() => {
        const loadInstitutionTypes = () => {
            institutionService
                .getTypes()
                .then((results) => {
                    setInstitutionTypes([
                        ...(results.filter((r) => r.isActive).sort((a, b) => a.title.localeCompare(b.title)) || []),
                    ]);
                })
                .catch(() => {
                    toast.error('Error loading institution types');
                });
        };

        const loadAuspices = () => {
            institutionService
                .getAuspice()
                .then((results) => {
                    setAuspices([
                        ...(results.filter((r) => r.isActive).sort((a, b) => a.title.localeCompare(b.title)) || []),
                    ]);
                })
                .catch(() => {
                    toast.error('Error loading auspices');
                });
        };

        loadInstitutionTypes();
        loadAuspices();
    }, [setInstitutionTypes, setAuspices]);

    // City / State / Country are required on create — mirrors the API guard added
    // alongside the systemadmin claim check (institution 9575 incident, 2026-06-12).
    const institutionSchema = yup
        .object({
            institutionId: yup.number().required().default(0),
            institutionTypeId: yup
                .number()
                .required('Institution Type is required')
                .min(1, 'Institution Type is required'),
            auspiceId: yup.number().required('Auspice is required').min(1, 'Auspice is required'),
            title: yup.string().required('Name is required'),
            city: yup.string().required('City is required'),
            state: yup.string().required('State is required'),
            address: yup.string().notRequired().nullable().default(null),
            address2: yup.string().notRequired().nullable().default(null),
            address3: yup.string().notRequired().nullable().default(null),
            address4: yup.string().notRequired().nullable().default(null),
            zip: yup.string().notRequired().nullable().default(null),
            country: yup.string().required('Country is required'),
            phone: yup.string().notRequired().nullable().default(null),
            extension: yup.string().notRequired().nullable().default(null),
            fax: yup.string().notRequired().nullable().default(null),
            webSite: yup.string().notRequired().nullable().default(null),
            ceoId: yup.number().notRequired().nullable().default(null),
            createdBy: yup.string().notRequired().nullable().default(null),
            dateCreated: yup.date().notRequired().nullable().default(null),
            lastEdited: yup.date().notRequired().nullable().default(null),
            editedBy: yup.string().notRequired().nullable().default(null),
            active: yup.boolean().required().default(true),
            preserveBillingInformation: yup.boolean().required().default(false),
            synchronizeCeoInstitution: yup.boolean().required().default(false),
            institutionType: yup.string().notRequired().nullable(),
        })
        .defined();

    const {
        handleSubmit,
        watch,
        setValue,
        control,
        formState: { errors },
    } = useForm({
        defaultValues: {} as Institution,
        resolver: yupResolver(institutionSchema as any),
    });

    const country = watch('country');

    useEffect(() => {
        if (country && country !== 'United States of America') {
            setIsInternational(true);
        } else {
            setIsInternational(false);
        }
    }, [country, setValue]);

    const submitForm = (values: Institution) => {
        const toastId = toast.info('Creating institution...');

        institutionService
            .saveInstitutions(new Institution(values))
            .then((result) => {
                toast.update(toastId, {
                    render: 'Institution created successfully',
                    type: 'success',
                });

                setNavigateToId(result.institutionId);
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Error creating institution',
                    type: 'error',
                });
            });
    };

    if (navigateToId && navigateToId > 0) {
        return <Redirect to={`/institution/${navigateToId}`} />;
    } else {
        return (
            <>
                <Form noValidate onSubmit={handleSubmit(submitForm)}>
                    <Card className={'mb-0'}>
                        <CardBody>
                            <h4>{`New Institution`}</h4>

                            <FormGroup>
                                <Label>{`Name`}</Label>
                                <Controller
                                    name="title"
                                    control={control}
                                    render={({ field }) => <Input type="text" {...field} value={field.value || ''} />}
                                />
                                {errors?.title?.message && (
                                    <span className={'text-danger'}>{errors.title.message}</span>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`Institution Type`}</Label>
                                <Controller
                                    name="institutionTypeId"
                                    control={control}
                                    render={({ field }) => (
                                        <Input type="select" {...field} value={field.value ?? ''}>
                                            <option value={-1}>{`Select an Institution Type`}</option>
                                            {institutionTypes?.map((t) => (
                                                <option key={t.institutionTypeId} value={t.institutionTypeId}>
                                                    {t.title}
                                                </option>
                                            ))}
                                        </Input>
                                    )}
                                />
                                {errors?.institutionTypeId?.message && (
                                    <span className={'text-danger'}>{errors.institutionTypeId.message}</span>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`Control`}</Label>
                                <Controller
                                    name="auspiceId"
                                    control={control}
                                    render={({ field }) => (
                                        <Input type="select" {...field} value={field.value ?? ''}>
                                            <option value={-1}>{`Select an Auspice`}</option>
                                            {auspices?.map((a) => (
                                                <option key={a.auspiceID} value={a.auspiceID}>
                                                    {a.title}
                                                </option>
                                            ))}
                                        </Input>
                                    )}
                                />
                                {errors?.auspiceId?.message && (
                                    <span className={'text-danger'}>{errors.auspiceId.message}</span>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`Phone`}</Label>
                                <Controller
                                    name="phone"
                                    control={control}
                                    render={({ field }) => <Input type="text" {...field} value={field.value || ''} />}
                                />
                                {errors?.phone?.message && (
                                    <span className={'text-danger'}>{errors.phone.message}</span>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`Extension`}</Label>
                                <Controller
                                    name="extension"
                                    control={control}
                                    render={({ field }) => <Input type="text" {...field} value={field.value || ''} />}
                                />
                                {errors?.extension?.message && (
                                    <span className={'text-danger'}>{errors.extension.message}</span>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`Fax`}</Label>
                                <Controller
                                    name="fax"
                                    control={control}
                                    render={({ field }) => <Input type="text" {...field} value={field.value || ''} />}
                                />
                                {errors?.fax?.message && <span className={'text-danger'}>{errors.fax.message}</span>}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`Website`}</Label>
                                <Controller
                                    name="webSite"
                                    control={control}
                                    render={({ field }) => <Input type="text" {...field} value={field.value || ''} />}
                                />
                                {errors?.webSite?.message && (
                                    <span className={'text-danger'}>{errors.webSite.message}</span>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`Address`}</Label>
                                <Controller
                                    name="address"
                                    control={control}
                                    render={({ field }) => <Input type="text" {...field} value={field.value || ''} />}
                                />
                                {errors?.address?.message && (
                                    <span className={'text-danger'}>{errors.address.message}</span>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`Address 2`}</Label>
                                <Controller
                                    name="address2"
                                    control={control}
                                    render={({ field }) => <Input type="text" {...field} value={field.value || ''} />}
                                />
                                {errors?.address2?.message && (
                                    <span className={'text-danger'}>{errors.address2.message}</span>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`Address 3`}</Label>
                                <Controller
                                    name="address3"
                                    control={control}
                                    render={({ field }) => <Input type="text" {...field} value={field.value || ''} />}
                                />
                                {errors?.address3?.message && (
                                    <span className={'text-danger'}>{errors.address3.message}</span>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`Address 4`}</Label>
                                <Controller
                                    name="address4"
                                    control={control}
                                    render={({ field }) => <Input type="text" {...field} value={field.value || ''} />}
                                />
                                {errors?.address4?.message && (
                                    <span className={'text-danger'}>{errors.address4.message}</span>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`City`}</Label>
                                <Controller
                                    name="city"
                                    control={control}
                                    render={({ field }) => <Input type="text" {...field} value={field.value || ''} />}
                                />
                                {errors?.city?.message && <span className={'text-danger'}>{errors.city.message}</span>}
                            </FormGroup>

                            <FormGroup>
                                <Label>{isInternational ? `State/Province` : `State`}</Label>
                                {isInternational && (
                                    <div>
                                        <Controller
                                            name="state"
                                            control={control}
                                            render={({ field }) => (
                                                <Input type="text" {...field} value={field.value || ''} />
                                            )}
                                        />
                                    </div>
                                )}
                                {!isInternational && (
                                    <div>
                                        <Controller
                                            control={control}
                                            name={'state'}
                                            render={({ field }) => (
                                                <Input type={'select'} {...field} value={field.value ?? ''}>
                                                    <option value={undefined}></option>
                                                    {States &&
                                                        States.map((s) => (
                                                            <option key={s.abbreviation} value={s.abbreviation}>
                                                                {s.abbreviation}
                                                            </option>
                                                        ))}
                                                </Input>
                                            )}
                                        />
                                    </div>
                                )}
                                {errors?.state?.message && (
                                    <span className={'text-danger'}>{errors.state.message}</span>
                                )}
                            </FormGroup>

                            <FormGroup>
                                <Label>{isInternational ? `Postal Code` : `Zip`}</Label>
                                <Controller
                                    name="zip"
                                    control={control}
                                    render={({ field }) => <Input type="text" {...field} value={field.value || ''} />}
                                />
                                {errors?.zip?.message && <span className={'text-danger'}>{errors.zip.message}</span>}
                            </FormGroup>

                            <FormGroup>
                                <Label>{`Country`}</Label>
                                <Controller
                                    control={control}
                                    name={'country'}
                                    render={({ field }) => (
                                        <Input type={'select'} {...field} value={field.value ?? ''}>
                                            <option value={undefined}></option>
                                            {Countries.map((c) => (
                                                <option key={c} value={c}>
                                                    {c}
                                                </option>
                                            ))}
                                        </Input>
                                    )}
                                />{' '}
                                {errors?.country?.message && (
                                    <span className={'text-danger'}>{errors.country.message}</span>
                                )}
                            </FormGroup>
                        </CardBody>
                        <CardFooter>
                            <Button color={'primary'} type={'submit'}>
                                {`Create Institution`}
                            </Button>
                            <Button color={'secondary'} type={'button'} className={'ml-2'} onClick={() => onCanceled()}>
                                {`Cancel`}
                            </Button>
                        </CardFooter>
                    </Card>
                </Form>
            </>
        );
    }
};

export default NewInstitution;
